import React from 'react';
import { useTranslation } from 'react-i18next';
import { getAnalyticsConsent, setAnalyticsConsent, syncAnalyticsProvider } from '../analytics';
import { useEscapeKey } from '../hooks/useEscapeKey';

const CONSENT_BANNER_KEY = 'dg_consent_banner_seen';

export function ConsentBanner(): React.ReactElement | null {
  const { t } = useTranslation();
  const [show, setShow] = React.useState(false);
  const consent = getAnalyticsConsent();

  React.useEffect(() => {
    if (typeof window === 'undefined') return;
    const seen = localStorage.getItem(CONSENT_BANNER_KEY);
    if (seen === '1' || consent) {
      setShow(false);
      return;
    }
    setShow(true);
  }, [consent]);

  const accept = React.useCallback(() => {
    setAnalyticsConsent(true);
    syncAnalyticsProvider();
    localStorage.setItem(CONSENT_BANNER_KEY, '1');
    setShow(false);
  }, []);

  const decline = React.useCallback(() => {
    setAnalyticsConsent(false);
    localStorage.setItem(CONSENT_BANNER_KEY, '1');
    setShow(false);
  }, []);

  useEscapeKey(show, decline);

  if (!show) return null;

  return (
    <div
      role="dialog"
      aria-label={t('consent.dialogLabel')}
      className="fixed inset-x-0 bottom-0 z-[80] border-t border-dg-border-subtle bg-dg-bg-surface/95 shadow-dg-nav backdrop-blur-md dark:border-slate-700 dark:bg-slate-900/95"
      style={{ paddingBottom: 'max(0.75rem, env(safe-area-inset-bottom))' }}
    >
      <div className="mx-auto flex max-w-3xl flex-col items-start gap-2.5 px-4 py-2.5 sm:flex-row sm:items-center sm:gap-3 sm:py-3">
        <p className="flex-1 text-xs leading-snug text-dg-fg-secondary dark:text-slate-200 sm:text-sm">
          {t('consent.body')}
        </p>
        <div className="flex w-full shrink-0 gap-2 sm:w-auto">
          <button
            type="button"
            onClick={accept}
            className="inline-flex min-h-[44px] flex-1 items-center justify-center rounded-dg-control bg-dg-primary-gradient px-4 text-sm font-semibold text-white shadow-dg-primary hover:shadow-dg-card-hover active:brightness-95 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-dg-primary-600 sm:flex-none"
          >
            {t('consent.accept')}
          </button>
          <button
            type="button"
            onClick={decline}
            className="inline-flex min-h-[44px] flex-1 items-center justify-center rounded-dg-control border border-dg-border-subtle bg-dg-bg-surface px-4 text-sm font-semibold text-dg-fg-primary hover:bg-dg-bg-muted hover:border-dg-border focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-dg-primary-600 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100 dark:hover:bg-slate-800 sm:flex-none"
          >
            {t('consent.decline')}
          </button>
        </div>
      </div>
    </div>
  );
}
